<template>
    <div v-if="banners?.length" :id="`js-slim-carousel-${carouselId}`" ref="carousel" class="slim-carousel hpBanner js-scroll-only-section">
        <div
            class="slim-carousel__container js-slim-carousel-scroll-container objects-banners-container"
            @touchstart="firstTouchX = $event.touches?.[0].screenX"
            @touchmove="lastTouchX = $event.touches?.[0].screenX"
            @touchend="changeSlideBySwipe(firstTouchX > lastTouchX ? 1 : -1)"
        >
            <div
                v-for="(item, index) in banners"
                :id="'banner-' + index"
                :key="item.id"
                ref="bannerElements"
                :data-id="item.id"
                :data-title="item.title"
                class="slim-carousel__item slim-carousel__item--banners banner"
                :class="{ 'banner--active': index === activeIndex }"
            >
                <nuxt-link class="hpBanner__link" :to="urlBannerRedirect(item.url)" :title="item.title">
                    <div class="hpBanner__data">
                        <h2 class="hpBanner__data-title">{{ item.title }}</h2>
                        <div class="hpBanner__data-perex" v-html="item.description"></div>
                        <button v-if="item.ctaText" class="hpBanner__data-button btn btn-primary">{{ item.ctaText }}</button>
                    </div>
                    <picture v-if="index === activeIndex" class="hpBanner__picture" data-not-lazy>
                        <source media="(max-width: 571px)" width="425" height="297" :srcset="item.mobileImage" />
                        <source
                            v-if="item.tabletImage !== null"
                            media="(min-width: 572px) and (max-width: 1023px)"
                            :srcset="item.tabletImage"
                        />
                        <source v-if="item.tabletImage !== null" :srcset="item.desktopImage" media="(min-width: 1024px)" />
                        <source v-else :srcset="item.desktopImage" media="(min-width: 572px)" />

                        <img
                            class="hpBanner__img"
                            :src="item.desktopImage"
                            :alt="item.title"
                            width="1920"
                            height="400"
                            fetchpriority="high"
                        />
                    </picture>
                    <picture v-else class="hpBanner__picture">
                        <source v-lazy-load media="(max-width: 571px)" :data-srcset="item.mobileImage" />
                        <source
                            v-if="item.tabletImage !== null"
                            v-lazy-load
                            media="(min-width: 572px) and (max-width: 1023px)"
                            :data-srcset="item.tabletImage"
                        />
                        <source v-if="item.tabletImage !== null" v-lazy-load media="(min-width: 1024px)" :data-srcset="item.desktopImage" />
                        <source v-else v-lazy-load media="(min-width: 572px)" :data-srcset="item.desktopImage" />

                        <img
                            v-lazy-load
                            class="hpBanner__img"
                            src="/nuxt-assets/images/lazy-load-error.svg"
                            :data-src="item.desktopImage"
                            :alt="item.title"
                            width="1920"
                            height="400"
                        />
                    </picture>
                </nuxt-link>
                <WarningMessageInBanner
                    v-if="item.warningMessage?.length"
                    :message="item.warningMessage"
                    :modal-id="`${item.id}–warningMessage`"
                />
            </div>
        </div>
        <div class="carousel-indicators">
            <a
                v-for="(banner, key) in banners"
                :key="key"
                href="#"
                :data-id="key"
                class="carousel-indicators__items js-slime-carousel-point"
                :class="{ 'point--active': key === activeIndex }"
                :aria-label="$t('slides.next')"
                @click="activeIndex = key"
            >
            </a>
        </div>

        <template v-if="showArrows">
            <a v-if="activeIndex > 0" class="arrow arrow__left" href="#" :aria-label="$t('slides.previous')" @click="changeSlide(-1)">
                <img
                    v-lazy-load
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    data-src="/nuxt-assets/icons/arrow_left.svg"
                    alt="left"
                    class="js-slime-carousel-left"
                    width="36"
                    height="36"
                />
            </a>
            <a
                v-if="activeIndex < banners.length - 1"
                class="arrow arrow__right"
                href="#"
                :aria-label="$t('slides.next')"
                @click="changeSlide(1)"
            >
                <img
                    v-lazy-load
                    src="/nuxt-assets/images/lazy-load-error.svg"
                    data-src="/nuxt-assets/icons/arrow_right.svg"
                    alt="right"
                    class="js-slime-carousel-right"
                    width="36"
                    height="36"
                />
            </a>
        </template>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import { useMatomoViewportTrack, useMatomoClickTrack } from '~/composables/useMatomo'
import WarningMessageInBanner from '~/components/elements/WarningMessageInBanner.vue'
import Banner from '~/models/components/Banner'

let id = 500

export default {
    components: { WarningMessageInBanner },
    props: {
        banners: {
            type: Array as PropType<Banner[]>,
            default: () => {},
        },

        showArrows: {
            type: Boolean,
            default: false,
        },

        typeNavigation: {
            type: String,
            default: null,
        },
    },

    data() {
        return {
            firstTouchX: 0,
            lastTouchX: 0,
            activeIndex: 0,
            carouselId: null,
        }
    },

    created() {
        id++
        this.carouselId = id
    },

    mounted() {
        if (this.$refs.bannerElements) {
            this.$refs.bannerElements.forEach((item: HTMLElement) => {
                useMatomoViewportTrack(item, {
                    category: 'Homepage Banner',
                    action: 'view',
                    name: item.dataset.title,
                    value: +(item.dataset.id ?? 0),
                })

                useMatomoClickTrack(item, {
                    category: 'Homepage Banner',
                    action: 'click',
                    name: item.dataset.title,
                    value: +(item.dataset.id ?? 0),
                })
            })
        }
    },

    updated() {},

    methods: {
        urlBannerRedirect(url: string) {
            if (url === '') {
                return ''
            }

            const urlObject = new URL(url)

            return `${urlObject.pathname}${urlObject.search}`
        },

        isValidTouch(): boolean {
            return this.lastTouchX !== 0 && Math.abs(this.firstTouchX - this.lastTouchX) > 30
        },

        changeSlideBySwipe(diff: number): void {
            if (this.isValidTouch()) {
                this.changeSlide(diff)
                this.lastTouchX = 0
            }
        },

        changeSlide(diff: number): void {
            if (this.activeIndex + diff < 0 || this.activeIndex + diff > this.banners.length - 1) {
                return
            }

            this.activeIndex += diff
        },
    },
}
</script>

<style lang="scss" scoped>
.hpBanner {
    background-color: #f5f5f5;
    max-width: 1920px;
    margin: auto;
    overflow: hidden;
    display: block;
    position: relative;
    height: 400px;

    @media (max-width: 572px) {
        height: 280px;
    }

    .banner {
        position: absolute;
        visibility: hidden;
        opacity: 0;
        transition: visibility 0s, opacity 1s linear;

        &--active {
            visibility: visible;
            opacity: 1;
        }
    }

    .carousel-indicators {
        display: flex;
        bottom: 0;
        margin-bottom: 125px;

        &__items {
            width: 10px;
            height: 10px;
            border-radius: 50px;
            background-color: rgba(105, 105, 105, 0.3);
            margin: 0 2px;

            &.point--active {
                background-color: $color-cta;
            }
        }

        @media (max-width: 572px) {
            margin-bottom: 32px;
        }
    }

    .active.carousel-item-left {
        display: block;
        transform: none;
        opacity: 0;
        transition: all 0.4s;
    }
}

.hpBanner__link {
    position: relative;
    text-decoration: none;
    display: block;
    height: 100%;
    width: 100%;

    @media (max-width: 572px) {
        height: 280px;
    }

    &:after {
        display: block;
        content: '';
        position: absolute;
        z-index: 1;
        bottom: 0;
        right: 0;
        left: 0;
        height: 50px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, $color-white 100%);
    }
}

.hpBanner__data {
    position: absolute;
    z-index: 2;
    bottom: 20px;
    left: 20px;
    right: 20px;
    top: 0;
    display: flex;
    align-items: baseline;
    text-align: left;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 572px) {
        width: 50%;
    }
    @media (min-width: 572px) {
        right: 0;
        left: 0;
        bottom: 60px;
        align-items: center;
        text-align: center;
    }
}

.hpBanner__data-title {
    font-size: 1.5em;

    @media (min-width: 572px) {
        margin-bottom: 1rem;
        font-size: 34px;
    }
}

.hpBanner__data-perex {
    margin-bottom: 1.5rem;
    font-size: 14px;
    color: $color-dark;

    @media (min-width: 572px) {
        font-size: 16px;
    }
}

.hpBanner__data-button {
    width: 100%;
    max-width: 180px;
}

.hpBanner__picture {
    text-align: center;
}

.hpBanner__img {
    height: auto;
    width: 100%;
    object-fit: cover;

    @media (min-width: 572px) {
        height: 400px;
        text-align: center;
    }
}

.slim-carousel__container.objects-banners-container {
    display: flex;
    height: auto;
    overflow-x: auto;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: 0;
    scroll-behavior: smooth;
    background: #fff;

    &::-webkit-scrollbar {
        -webkit-appearance: none;
        height: 10px;
    }

    &:hover::-webkit-scrollbar-thumb {
        border: 0;
        background-color: $color-grey-semi-dark;
        border-radius: 10px;
        cursor: pointer;

        @media (min-width: 890px) {
            display: none;
        }
    }
}

.slim-carousel__item {
    position: relative;

    &--banners {
        display: flex;
        visibility: hidden;
        flex: 0 0 100%;
        width: 100%;
        overflow-y: hidden;
    }
}

.arrow {
    opacity: 0.7;
    position: absolute;
    top: calc(50% - 18px);
    z-index: 10;

    &__left {
        left: 1rem;
    }

    &__right {
        right: 1rem;
    }

    @media (max-width: 572px) {
        display: none;
    }
}
</style>
